import { useForm, useWatch, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import i18next from 'i18next';
import { isEmpty, pick, get } from 'lodash-es';
import * as React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';

import API from '../../../../../lib/API';
import { sendVerificationCode } from '../../utils/api';
import { error as showError } from '../../utils/i18n';
import {
  trackOptimizelyCustomEvent,
  trackRegistrationEvent,
} from '../../utils/tracker';
import {
  Checkbox,
  DisabledButton,
  Errors,
  FormLabel,
  SubmitButton,
  TextInput,
  RadioCard,
  RadioCardClassNamesProps,
  Textarea,
} from '../common/form';
import { withUnloadConfirm } from '../common/composer/withUnloadConfirm';
import {
  authenticityToken,
  isJunior,
  isHighSchool,
  isHighSchoolGraduate,
} from '.';
import {
  RootStateProps,
  LearnerAndAccountRegistrationRootState,
  BasicLearnerAndAccount,
} from '../../interfaces/basic';
import { Gender, isFreeGender } from '../../interfaces/common';
import LearnerAndAccountRegistrationFormSchema from '../../validations/basic_or_coaching/learnerAndAccountRegistrationFormSchema';
import LoginNavigationModal from './LoginNavigationModal';
import SelectedGradePanel from './SelectedGradePanel';
import {
  useFormEvent,
  type ValidateFillItemResult,
} from '../common/form/useFormEvent';

interface FormValues {
  learner_account: BasicLearnerAndAccount;
  validatedLearnerAccount: boolean;
  sentMailToEnteredEmail: boolean;
  consentId: string;
  gradeCode: string;
  unexpected_error?: string;
}

type FormProps = RootStateProps<LearnerAndAccountRegistrationRootState>;

const LearnerAndAccountRegistrationForm: React.FC<FormProps> = ({
  rootState,
  setRootState,
}) => {
  const navigate = useNavigate();
  const formRef = React.useRef<HTMLFormElement>(null);
  const {
    setValue,
    setError,
    getValues,
    handleSubmit,
    trigger,
    control,
    clearErrors,
    formState: { errors, touchedFields, isValid, isSubmitting },
  } = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      ...pick(
        rootState,
        'learner_account',
        'validatedLearnerAccount',
        'sentMailToEnteredEmail',
        'consentId',
        'gradeCode',
      ),
      unexpected_error: '',
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(
      LearnerAndAccountRegistrationFormSchema({
        isJuniorGrade: isJunior(rootState.learner_account.grade_id),
      }),
      {},
      { mode: 'async' },
    ),
  });

  const log = React.useCallback(
    (key: string, data: Record<string, unknown>) => {
      const params = document.getElementById('tracking-data-params');
      if (!params) {
        throw Error('#tracking-data-params does not exist!');
      }
      const efo_version = params.getAttribute('data-default-efo-version');
      const firebaseClient = (window as any).firebaseClient;
      if (firebaseClient) {
        firebaseClient.trackEvent(null, key, {
          grade_code: rootState.gradeCode,
          efo_version,
          ...data,
        });
      } else {
        console.log(
          `[firebaseClient.trackEvent] ${key}: ${JSON.stringify({
            grade_code: rootState.gradeCode,
            efo_version,
            ...data,
          })}`,
        );
      }
    },
    [rootState.gradeCode],
  );

  useFormEvent({
    ref: formRef,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    control,
    handlers: {
      onValidateFillItem: React.useCallback(
        (key: string, value: ValidateFillItemResult) => {
          log('fill_registration_form_result', {
            field_name: key,
            success: value.success,
            error_type: value.success ? null : value.error.type,
          });
        },
        [log],
      ),
      onFieldFocusIn: React.useCallback(
        (key: string) => {
          log('fill_registration_form_item_start', {
            field_name: key,
          });
        },
        [log],
      ),
      onFieldFocusOut: React.useCallback(
        (key: string) => {
          log('fill_registration_form_item_finish', {
            field_name: key,
          });
        },
        [log],
      ),
    },
  });

  React.useEffect(() => {
    trigger();
  }, [trigger]);

  // 条件付きレンダリングのために個別で監視
  const selectedGender = useWatch({ control, name: 'learner_account.gender' });

  // 性別自由記入情報の入力値保持
  const lastGenderRef = React.useRef<Gender>(undefined);
  const lastGenderExpressionRef = React.useRef<string | undefined>(undefined);
  React.useEffect(() => {
    if (isFreeGender(lastGenderRef.current)) {
      lastGenderExpressionRef.current = getValues(
        'learner_account.gender_expression',
      );
    }

    if (isFreeGender(selectedGender)) {
      if (!isEmpty(lastGenderExpressionRef.current)) {
        setValue(
          'learner_account.gender_expression',
          lastGenderExpressionRef.current,
        );
      }
    } else {
      setValue('learner_account.gender_expression', undefined);
      clearErrors('learner_account.gender_expression');
    }

    lastGenderRef.current = selectedGender;
  }, [selectedGender, clearErrors, getValues, setValue]);

  const genderRadioCardClassNames: RadioCardClassNamesProps = {
    radioCard: 'gender-radio-card',
  };

  // TODO: Extract the common logic from AccountForm and use it
  const sendVerificationCodeAfterValidate =
    React.useCallback(async (): Promise<void> => {
      const response = await sendVerificationCode(
        getValues('learner_account.email'),
        rootState.learner_account.grade_id,
      );

      if (response.status === 201) {
        const json = await response.json();
        const consentId = json.consent_id;
        setRootState({
          ...rootState,
          ...pick(getValues(), 'learner_account'),
          validatedLearnerAccount: true,
          sentMailToEnteredEmail: true,
          consentId,
        });

        trackRegistrationEvent(3, rootState.gradeCode);
        trackOptimizelyCustomEvent('learn_registration_completed');
        trackOptimizelyCustomEvent('support_registration_completed');
        navigate('/verification');
        return;
      }

      if (response.status === 400) {
        const json = await response.json();
        setValue('unexpected_error', json.error);
        return;
      }

      setValue(
        'unexpected_error',
        i18next.t('registration.sign_up.new.errors.unexpected_error'),
      );
    }, [getValues, navigate, rootState, setRootState, setValue]);

  const handleLearnerAccountSubmit =
    React.useCallback(async (): Promise<void> => {
      try {
        setValue('unexpected_error', '');

        const response = await API.post(
          '/registration/accounts/validate_learner_and_account',
          pick(getValues(), 'learner_account'),
          { 'X-CSRF-Token': authenticityToken },
        );

        if (response.status === 200) {
          setRootState({
            ...rootState,
            ...pick(getValues(), 'learner_account', 'gradeCode'),
            validatedLearnerAccount: true,
          });

          // TODO: design event tracking
          await sendVerificationCodeAfterValidate();
          return;
        }

        if (response.status === 400) {
          const json = await response.json();
          const keys = Object.keys(json.errors);
          for (const key of keys) {
            const msg = json.errors[key];
            // touched状態にならないと画面上は表示されない
            setError(
              key as keyof FormValues,
              { type: 'api', message: msg },
              { shouldFocus: true },
            );
          }
          return;
        }

        setValue(
          'unexpected_error',
          i18next.t('registration.sign_up.new.errors.unexpected_error'),
        );
      } catch {
        // handleSubmit内で例外が発生するとisSubmittingがfalseにならない問題があるためcatchする
        // see https://github.com/orgs/react-hook-form/discussions/10103
      }
    }, [
      setValue,
      getValues,
      rootState,
      setError,
      // navigate,
      setRootState,
      sendVerificationCodeAfterValidate,
    ]);

  if (
    isEmpty(rootState.learner_account.grade_id) ||
    isEmpty(rootState.gradeCode)
  ) {
    return <Navigate to="/" />;
  }

  const showLoginNavigationModal = (): boolean => {
    return (
      get(errors, 'learner_account.email')?.message ===
      showError('email_uniqueness_error')
    );
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(handleLearnerAccountSubmit)}
      onChange={() => {
        setValue('validatedLearnerAccount', false, { shouldValidate: true });
      }}
      data-test-form-id="learner-registration-user"
    >
      <div className="sm-up-size-xl xs-down-size-lg bold text-center xs-down-m-t xs-down-m-b-md sm-up-m-y-lg">
        {i18next.t('registration.sign_up.new.learner_registration_user.title')}
      </div>
      <div className="row sm-up-m-t-xl xs-down-m-t-lg sm-up-m-b-lg m-b-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 size-sm">
          <SelectedGradePanel gradeLabel={rootState.gradeLabel} />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <FormLabel
            errors={errors}
            getValues={getValues}
            label={i18next.t('register.form.label.name')}
            fieldNames={[
              'learner_account.last_name',
              'learner_account.first_name',
            ]}
          />
          <span className="size-sm bold">
            {i18next.t(
              'registration.sign_up.new.learner_registration_user.learners_info',
            )}
          </span>
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <div className="row-sm">
            <div className="col-xs-6">
              <Controller
                control={control}
                name="learner_account.last_name"
                render={({
                  field: { onBlur, onChange, value, name },
                  fieldState: { error },
                }) => (
                  <TextInput
                    error={error}
                    value={value}
                    fieldName={name}
                    onChange={onChange}
                    autoFocus={isEmpty(value)}
                    onBlur={onBlur}
                  />
                )}
              />
            </div>
            <div className="col-xs-6">
              <Controller
                control={control}
                name="learner_account.first_name"
                render={({
                  field: { onChange, onBlur, value, name },
                  fieldState: { error },
                }) => (
                  <TextInput
                    error={error}
                    value={value}
                    fieldName={name}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </div>
            <div className="col-xs-12">
              <Errors
                errors={errors}
                touchedFields={touchedFields}
                fieldNames={[
                  'learner_account.last_name',
                  'learner_account.first_name',
                ]}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row m-t-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <FormLabel
            errors={errors}
            getValues={getValues}
            label={i18next.t('register.form.label.kana')}
            fieldNames={[
              'learner_account.last_name_kana',
              'learner_account.first_name_kana',
            ]}
          />
          <span className="size-sm bold">
            {i18next.t(
              'registration.sign_up.new.learner_registration_user.learners_info',
            )}
          </span>
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <div className="row-sm">
            <div className="col-xs-6">
              <Controller
                control={control}
                name="learner_account.last_name_kana"
                render={({
                  field: { onChange, onBlur, value, name },
                  fieldState: { error },
                }) => (
                  <TextInput
                    error={error}
                    value={value}
                    fieldName={name}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </div>
            <div className="col-xs-6">
              <Controller
                control={control}
                name="learner_account.first_name_kana"
                render={({
                  field: { onChange, onBlur, value, name },
                  fieldState: { error },
                }) => (
                  <TextInput
                    error={error}
                    value={value}
                    fieldName={name}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
            </div>
            <div className="col-xs-12">
              <Errors
                errors={errors}
                touchedFields={touchedFields}
                fieldNames={[
                  'learner_account.last_name_kana',
                  'learner_account.first_name_kana',
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 性別 */}
      {!isJunior(rootState.learner_account.grade_id) && (
        <>
          <div className="row m-t-md">
            <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 m-b-sm">
              <FormLabel
                errors={errors}
                getValues={getValues}
                label={i18next.t('register.form.label.gender')}
                fieldNames={[
                  'learner_account.gender',
                  'learner_account.gender_expression',
                ]}
                optionalFieldNames={'learner_account.gender_expression'}
              />
            </div>
            <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 m-t-sm">
              <div className="gender-radio-cards">
                <Controller
                  control={control}
                  name="learner_account.gender"
                  render={({ field: { onChange, value } }) => (
                    <RadioCard
                      value={'m'}
                      selectedValue={value}
                      label={i18next.t('register.form.label.gender_m')}
                      fieldName={'learner_account_gender'}
                      onChange={onChange}
                      classNames={genderRadioCardClassNames}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="learner_account.gender"
                  render={({ field: { onChange, value } }) => (
                    <RadioCard
                      value={'f'}
                      selectedValue={value}
                      label={i18next.t('register.form.label.gender_f')}
                      fieldName={'learner_account_gender'}
                      onChange={onChange}
                      classNames={genderRadioCardClassNames}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="learner_account.gender"
                  render={({ field: { onChange, value } }) => (
                    <RadioCard
                      value={'no_answer'}
                      selectedValue={value}
                      label={i18next.t('register.form.label.gender_no_answer')}
                      fieldName={'learner_account_gender'}
                      onChange={onChange}
                      classNames={genderRadioCardClassNames}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="learner_account.gender"
                  render={({ field: { onChange, value } }) => (
                    <RadioCard
                      value={'free'}
                      selectedValue={value}
                      label={i18next.t('register.form.label.gender_free')}
                      fieldName={'learner_account_gender'}
                      onChange={onChange}
                      classNames={genderRadioCardClassNames}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          {isFreeGender(selectedGender) && (
            <div className="row m-t">
              <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
                <Controller
                  control={control}
                  name="learner_account.gender_expression"
                  render={({
                    field: { onBlur, onChange, value, name },
                    fieldState: { error },
                  }) => (
                    <Textarea
                      autoFocus={isFreeGender(selectedGender)}
                      error={error}
                      value={value}
                      fieldName={name}
                      onBlur={onBlur}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
              <Errors
                errors={errors}
                touchedFields={touchedFields}
                fieldNames={[
                  'learner_account.gender',
                  'learner_account.gender_expression',
                ]}
              />
            </div>
          </div>
          {isFreeGender(selectedGender) && (
            <div className="row">
              <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 size-sm gray-dark m-t">
                {i18next.t(
                  'registration.sign_up.new.learner_registration_user.gender_expression.caution',
                )}
              </div>
            </div>
          )}
        </>
      )}

      <div className="row m-t-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <FormLabel
            errors={errors}
            getValues={getValues}
            label={i18next.t('register.form.label.tel')}
            fieldNames="learner_account.phone_number"
          />
          <span className="size-sm bold">
            {i18next.t(
              'registration.sign_up.new.learner_registration_user.parents_info',
            )}
          </span>
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <Controller
            control={control}
            name="learner_account.phone_number"
            render={({
              field: { onChange, onBlur, value, name },
              fieldState: { error },
            }) => (
              <TextInput
                error={error}
                value={value}
                fieldName={name}
                onChange={onChange}
                onBlur={onBlur}
                type="tel"
              />
            )}
          />
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <Errors
            errors={errors}
            touchedFields={touchedFields}
            fieldNames="learner_account.phone_number"
          />
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 size-sm gray-dark">
          {i18next.t(
            'registration.sign_up.new.learner_registration_user.service_info',
          )}
        </div>
      </div>
      <div className="row m-t-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <FormLabel
            errors={errors}
            getValues={getValues}
            label={i18next.t('register.form.label.email')}
            fieldNames="learner_account.email"
          />
          <span className="size-sm bold">
            {i18next.t(
              'registration.sign_up.new.learner_registration_user.parents_info',
            )}
          </span>
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <Controller
            control={control}
            name="learner_account.email"
            render={({
              field: { onChange, onBlur, value, name },
              fieldState: { error },
            }) => (
              <TextInput
                error={error}
                value={value}
                fieldName={name}
                type="email"
                onChange={(e) => {
                  onChange(e);
                  setValue('sentMailToEnteredEmail', false);
                }}
                onBlur={onBlur}
              />
            )}
          />
          <Errors
            errors={errors}
            touchedFields={touchedFields}
            fieldNames="learner_account.email"
          />
          <LoginNavigationModal show={showLoginNavigationModal()} />
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 size-sm gray-dark">
          {i18next.t(
            'registration.sign_up.new.learner_registration_user.service_info',
          )}
          <br />
          {i18next.t(
            'registration.sign_up.new.learner_registration_user.support_web_login',
          )}
        </div>
      </div>
      <div className="row m-t-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <FormLabel
            errors={errors}
            getValues={getValues}
            label={i18next.t('register.form.label.pass')}
            fieldNames={[
              'learner_account.password',
              'learner_account.password_confirmation',
            ]}
          />
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 m-b">
          <Controller
            control={control}
            name="learner_account.password"
            render={({
              field: { onChange, onBlur, value, name },
              fieldState: { error },
            }) => (
              <TextInput
                error={error}
                value={value}
                fieldName={name}
                onChange={onChange}
                onBlur={onBlur}
                type="password"
              />
            )}
          />
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <Controller
            control={control}
            name="learner_account.password_confirmation"
            render={({
              field: { onChange, onBlur, value, name },
              fieldState: { error },
            }) => (
              <TextInput
                error={error}
                value={value}
                fieldName={name}
                onChange={(e) => {
                  onChange(e);
                  trigger('learner_account.password');
                }}
                onBlur={onBlur}
                type="password"
              />
            )}
          />
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <Errors
            errors={errors}
            touchedFields={touchedFields}
            fieldNames={[
              'learner_account.password',
              'learner_account.password_confirmation',
            ]}
          />
        </div>
      </div>
      <div className="row m-y-md">
        <div
          className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 size-sm gray-dark"
          dangerouslySetInnerHTML={{
            __html: i18next.t(
              'registration.sign_up.new.learner_registration_user.password.caution',
            ),
          }}
        />
      </div>
      <div className="row m-t-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <FormLabel
            errors={errors}
            getValues={getValues}
            label={i18next.t('register.form.label.inputted_by_parents')}
            fieldNames="learner_account.inputted_by_parents"
          />
        </div>
      </div>
      <div className="row">
        <div
          className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 m-t"
          id="inputted-by-parents"
        >
          <Controller
            control={control}
            name="learner_account.inputted_by_parents"
            render={({ field: { onChange, onBlur, name } }) => (
              <Checkbox
                getValues={getValues}
                fieldName={name}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
        </div>
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <Errors
            errors={errors}
            touchedFields={touchedFields}
            fieldNames="learner_account.inputted_by_parents"
          />
        </div>
      </div>
      <div className="row m-y-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 size-sm gray-dark">
          {i18next.t(
            'registration.sign_up.new.register_learner_for_study_web.inputted_by_parents_complement',
          )}
        </div>
      </div>
      <div className="row m-t-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <label className="bold size-md m-b-0">
            <span className="label size-sm bold label-gray">
              {i18next.t('register.form.label.optional')}
            </span>
            <span className="m-l">
              {i18next.t('register.form.label.email_reception_settings')}
            </span>
          </label>
        </div>
      </div>
      <div className="row m-t-md">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
          <Controller
            control={control}
            name="learner_account.receive_latest_info"
            render={({ field: { onChange, onBlur, name } }) => (
              <Checkbox
                getValues={getValues}
                fieldName={name}
                onChange={onChange}
                onBlur={onBlur}
                notesName="receive_latest_info_notes"
              />
            )}
          />
        </div>
      </div>
      {(isHighSchool(rootState.learner_account.grade_id) ||
        isHighSchoolGraduate(rootState.learner_account.grade_id)) && (
        <div className="row m-t-md">
          <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3">
            <Controller
              control={control}
              name="learner_account.receive_webdm_remind_permission"
              render={({ field: { onChange, onBlur, name } }) => (
                <Checkbox
                  getValues={getValues}
                  fieldName={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  notesName="receive_webdm_remind_permission_notes"
                />
              )}
            />
          </div>
        </div>
      )}
      <div
        className="text-center"
        dangerouslySetInnerHTML={{
          __html: i18next.t('register.form.label.terms_and_privacy_policy', {
            terms_url:
              'https://cdn.p.recruit.co.jp/terms/stc-t-1001/index.html',
            privacy_url:
              'https://cdn.p.recruit.co.jp/terms/cmn-t-1001/index.html?p=pp044',
          }),
        }}
      />
      {getValues('unexpected_error') && (
        <div className="row">
          <div className="col-sm-offset-3 col-sm-6 col-xs-12 p-x">
            <div className="alert alert-danger">
              {getValues('unexpected_error')}
            </div>
          </div>
        </div>
      )}
      <div className="row sm-up-m-t-md xs-down-m-t-md sm-up-m-b-md xs-down-m-b">
        <div className="col-xs-12 col-sm-10 col-sm-offset-1 col-lg-6 col-lg-offset-3 col-md-6 col-md-offset-3">
          {isValid ? (
            <SubmitButton
              isSubmitting={isSubmitting}
              label={i18next.t(
                'registration.sign_up.new.learner_registration_user.submit',
              )}
              buttonClassNames="ytm-new_next ytm-new_confirm"
            />
          ) : (
            <DisabledButton
              label={i18next.t(
                'registration.sign_up.new.learner_registration_user.not_ready_to_submit',
              )}
              buttonClassNames="p-y-md"
            />
          )}
        </div>
      </div>
    </form>
  );
};

export default withUnloadConfirm(LearnerAndAccountRegistrationForm);
